import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { sessionsAPI, setupAutoSave } from '../../api/sessions';
import type { SurveySession } from '../../api/sessions';
import type { Survey, Question } from '../../types';

interface SessionManagerProps {
  survey: Survey;
  questions: Question[];
  sessionId?: string | null;
  onComplete?: () => void;
}

export const SessionManager: React.FC<SessionManagerProps> = ({ 
  survey, 
  questions,
  sessionId,
  onComplete 
}) => {
  const [session, setSession] = useState<SurveySession | null>(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [responses, setResponses] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [timeSpent, setTimeSpent] = useState(0);
  const navigate = useNavigate();
  const startTimeRef = useRef<Date>(new Date());
  const cleanupAutoSaveRef = useRef<(() => void) | null>(null);

  // Load or create session
  useEffect(() => {
    loadSession();
    
    // Track time spent
    const interval = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startTimeRef.current.getTime()) / 1000);
      setTimeSpent(elapsed);
    }, 1000);

    return () => {
      clearInterval(interval);
      // Cleanup auto-save on unmount
      if (cleanupAutoSaveRef.current) {
        cleanupAutoSaveRef.current();
      }
    };
  }, [survey.id, sessionId]);

  const loadSession = async () => {
    try {
      setLoading(true);
      
      let activeSession: SurveySession;
      
      if (sessionId) {
        // Load the session that was already started
        activeSession = await sessionsAPI.getSession(sessionId);
        setResponses(activeSession.responses || {});
        setCurrentQuestionIndex(activeSession.current_question_index || 0);
        setTimeSpent(activeSession.time_spent_seconds || 0);
      } else {
        // Check if user can attempt
        const canAttempt = await sessionsAPI.checkCanAttempt(survey.id);
        
        if (!canAttempt.can_attempt) {
          setError(canAttempt.reason || 'Cannot attempt this survey');
          setLoading(false);
          return;
        }

        if (canAttempt.has_active_session && canAttempt.active_session_id) {
          // Resume existing session
          activeSession = await sessionsAPI.getSession(canAttempt.active_session_id);
          setResponses(activeSession.responses || {});
          setCurrentQuestionIndex(activeSession.current_question_index || 0);
          setTimeSpent(activeSession.time_spent_seconds || 0);
        } else {
          // Start new session
          activeSession = await sessionsAPI.startSession(survey.id);
        }
      }

      setSession(activeSession);
      
      // Set up auto-save
      cleanupAutoSaveRef.current = setupAutoSave(
        activeSession.id,
        () => ({
          responses,
          current_question_index: currentQuestionIndex,
          time_spent_seconds: timeSpent,
          progress_percentage: calculateProgress()
        }),
        5000 // Auto-save every 5 seconds
      );
      
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load session');
    } finally {
      setLoading(false);
    }
  };

  const calculateProgress = useCallback(() => {
    if (questions.length === 0) return 0;
    const answered = Object.keys(responses).filter(key => responses[key] !== null).length;
    return (answered / questions.length) * 100;
  }, [responses, questions.length]);

  const handleAnswer = (questionId: string, answer: any) => {
    setResponses(prev => ({
      ...prev,
      [questionId]: answer
    }));
    setSaving(true);
    
    // Trigger manual save after answer
    setTimeout(async () => {
      if (session) {
        try {
          await sessionsAPI.saveProgress(session.id, {
            responses: { [questionId]: answer },
            current_question_index: currentQuestionIndex,
            progress_percentage: calculateProgress()
          });
          setSaving(false);
        } catch (err) {
          console.error('Failed to save progress:', err);
          setSaving(false);
        }
      }
    }, 500);
  };

  const handleNext = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    if (!session) return;
    
    try {
      setLoading(true);
      await sessionsAPI.completeSession(session.id, responses);
      
      // Cleanup auto-save
      if (cleanupAutoSaveRef.current) {
        cleanupAutoSaveRef.current();
      }
      
      if (onComplete) {
        onComplete();
      } else {
        navigate(`/surveys/${survey.id}/complete?attempt=${session.attempt_number}`);
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to submit survey');
      setLoading(false);
    }
  };

  const handleAbandon = async () => {
    if (!session) return;
    
    if (!confirm('Are you sure you want to abandon this survey? Your progress will be saved but the session will be closed.')) {
      return;
    }
    
    try {
      await sessionsAPI.abandonSession(session.id);
      
      // Cleanup auto-save
      if (cleanupAutoSaveRef.current) {
        cleanupAutoSaveRef.current();
      }
      
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to abandon session');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-foreground-muted">Loading session...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-error-light border border-error text-error px-4 py-3 rounded">
        {error}
        <button 
          onClick={() => navigate('/dashboard')}
          className="ml-4 text-error underline"
        >
          Return to Dashboard
        </button>
      </div>
    );
  }

  if (!session || questions.length === 0) {
    return <div>No session or questions available</div>;
  }

  const currentQuestion = questions[currentQuestionIndex];
  const progress = calculateProgress();
  const isLastQuestion = currentQuestionIndex === questions.length - 1;
  const canSubmit = Object.keys(responses).length === questions.length;

  return (
    <div className="max-w-3xl mx-auto p-6">
      {/* Session Header */}
      <div className="bg-surface rounded-lg shadow-md p-6 mb-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold">{survey.title}</h2>
          <div className="text-sm text-foreground-muted">
            Attempt {session.attempt_number}
            {survey.min_days_between_attempts ? ` (${survey.min_days_between_attempts}d cooldown)` : ''}
          </div>
        </div>
        
        {/* Progress Bar */}
        <div className="mb-4">
          <div className="flex justify-between text-sm text-foreground-muted mb-1">
            <span>Question {currentQuestionIndex + 1} of {questions.length}</span>
            <span>{Math.round(progress)}% complete</span>
          </div>
          <div className="w-full bg-border-light rounded-full h-2">
            <div 
              className="bg-primary h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
        
        {/* Save Indicator */}
        <div className="flex justify-between items-center text-sm">
          <div className="text-foreground-muted">
            Time: {Math.floor(timeSpent / 60)}:{(timeSpent % 60).toString().padStart(2, '0')}
          </div>
          {saving && (
            <div className="text-success">
              <span className="animate-pulse">● Progress saved</span>
            </div>
          )}
        </div>
      </div>

      {/* Question Display */}
      <div className="bg-surface rounded-lg shadow-md p-6 mb-6">
        <h3 className="text-lg font-semibold mb-4">
          {currentQuestion.text}
          {currentQuestion.required && <span className="text-error ml-1">*</span>}
        </h3>
        
        {/* Question Input based on type */}
        {currentQuestion.type === 'BOOLEAN' && (
          <div className="space-y-2">
            <label className="flex items-center">
              <input
                type="radio"
                name={`question-${currentQuestion.id}`}
                value="true"
                checked={responses[currentQuestion.id] === true}
                onChange={() => handleAnswer(currentQuestion.id, true)}
                className="mr-2"
              />
              Yes
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                name={`question-${currentQuestion.id}`}
                value="false"
                checked={responses[currentQuestion.id] === false}
                onChange={() => handleAnswer(currentQuestion.id, false)}
                className="mr-2"
              />
              No
            </label>
          </div>
        )}
        
        {currentQuestion.type === 'TEXT' && (
          <textarea
            value={responses[currentQuestion.id] || ''}
            onChange={(e) => handleAnswer(currentQuestion.id, e.target.value)}
            className="w-full p-2 border rounded-md"
            rows={4}
            placeholder="Enter your answer..."
          />
        )}
        
        {currentQuestion.type === 'RATING' && (
          <div className="flex space-x-2">
            {[1, 2, 3, 4, 5].map(rating => (
              <button
                key={rating}
                onClick={() => handleAnswer(currentQuestion.id, rating)}
                className={`px-4 py-2 rounded-md transition-colors ${
                  responses[currentQuestion.id] === rating
                    ? 'bg-primary text-white'
                    : 'bg-border-light hover:bg-border'
                }`}
              >
                {rating}
              </button>
            ))}
          </div>
        )}
        
        {currentQuestion.type === 'SINGLE_CHOICE' && currentQuestion.options && (
          <div className="space-y-2">
            {currentQuestion.options.map((option, index) => (
              <label key={index} className="flex items-center">
                <input
                  type="radio"
                  name={`question-${currentQuestion.id}`}
                  value={option}
                  checked={responses[currentQuestion.id] === option}
                  onChange={() => handleAnswer(currentQuestion.id, option)}
                  className="mr-2"
                />
                {option}
              </label>
            ))}
          </div>
        )}

        {currentQuestion.type === 'MULTIPLE_CHOICE' && currentQuestion.options && (
          <div className="space-y-2">
            {currentQuestion.options.map((option, index) => {
              const selected: string[] = responses[currentQuestion.id] || [];
              const isChecked = selected.includes(option);
              return (
                <label key={index} className="flex items-center">
                  <input
                    type="checkbox"
                    name={`question-${currentQuestion.id}`}
                    value={option}
                    checked={isChecked}
                    onChange={() => {
                      const updated = isChecked
                        ? selected.filter((v: string) => v !== option)
                        : [...selected, option];
                      handleAnswer(currentQuestion.id, updated);
                    }}
                    className="mr-2"
                  />
                  {option}
                </label>
              );
            })}
          </div>
        )}

        {currentQuestion.type === 'COUNTRY' && currentQuestion.options && (
          <select
            value={responses[currentQuestion.id] || ''}
            onChange={(e) => handleAnswer(currentQuestion.id, e.target.value)}
            className="w-full p-2 border rounded-md"
          >
            <option value="">Select a country...</option>
            {currentQuestion.options.map((option, index) => (
              <option key={index} value={option}>
                {option}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Navigation Buttons */}
      <div className="flex justify-between">
        <div>
          <button
            onClick={handlePrevious}
            disabled={currentQuestionIndex === 0}
            className="px-4 py-2 bg-border text-foreground rounded-md hover:bg-foreground-muted disabled:opacity-50 disabled:cursor-not-allowed mr-2"
          >
            Previous
          </button>
          <button
            onClick={handleAbandon}
            className="px-4 py-2 bg-error text-white rounded-md hover:bg-error"
          >
            Save & Exit
          </button>
        </div>
        
        <div>
          {!isLastQuestion ? (
            <button
              onClick={handleNext}
              className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark"
            >
              Next
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={!canSubmit}
              className="px-4 py-2 bg-success text-white rounded-md hover:bg-success disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Submit Survey
            </button>
          )}
        </div>
      </div>
    </div>
  );
};